import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { AuthUser, StaffUser, SystemStatus } from "../types";
import { Modal } from "../components/Modal";
import { initials } from "../components/util";

function randomPassword(): string {
  const abc = "abcdefghijkmnopqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const bytes = crypto.getRandomValues(new Uint8Array(14));
  return Array.from(bytes, (b) => abc[b % abc.length]).join("");
}

export function UsersPage({
  me,
  status,
}: {
  me: AuthUser;
  status: SystemStatus | null;
}) {
  const mailReady = Boolean(status?.mail.configured);
  const [users, setUsers] = useState<StaffUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [creating, setCreating] = useState(false);
  const [passwordFor, setPasswordFor] = useState<StaffUser | null>(null);
  const [linkFor, setLinkFor] = useState<StaffUser | null>(null);
  const [editing, setEditing] = useState<StaffUser | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .listUsers()
      .then((u) => {
        setUsers(u);
        setError("");
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    setError("");
    setNotice("");
    try {
      await fn();
      setNotice(msg);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Сотрудники</h1>
          <p>
            Кто может входить в сервис. Регистрации с улицы нет — учётные записи
            заводите здесь и передавайте логин с паролем лично.
          </p>
        </div>
        <button className="primary" onClick={() => setCreating(true)}>
          + Добавить сотрудника
        </button>
      </div>

      {notice && <div className="banner blue">{notice}</div>}
      {error && <div className="banner red">{error}</div>}

      <div className="card">
        <div className="card-head">
          <h3>Учётные записи</h3>
          <span className="muted small">{users.length}</span>
        </div>

        {loading && users.length === 0 ? (
          <div className="empty">
            <span className="spinner" /> Загружаю…
          </div>
        ) : (
          <div className="table-wrap"><table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Логин</th>
                <th>Права</th>
                <th>Доступ</th>
                <th>Телеграм</th>
                <th>Последний вход</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = u.id === me.id;
                return (
                  <tr key={u.id} style={{ opacity: u.disabled ? 0.55 : 1 }}>
                    <td>
                      <div className="row" style={{ gap: 10 }}>
                        <span className="avatar">{initials(u.name)}</span>
                        <div>
                          <div>
                            {u.name}
                            {isMe && <span className="muted small"> — это вы</span>}
                          </div>
                          <div className="muted small">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="mono">{u.login}</td>
                    <td>
                      <span className={`badge ${u.role === "admin" ? "blue" : ""}`}>
                        {u.role === "admin" ? "администратор" : "сотрудник"}
                      </span>
                      {u.role !== "admin" && (
                        <label
                          className="row small nowrap"
                          style={{ gap: 6, marginTop: 6, cursor: "pointer", flexWrap: "nowrap" }}
                          title="Видит только тех клиентов, которых внёс сам или которых ему передали"
                        >
                          <input
                            type="checkbox"
                            checked={u.own_leads_only}
                            onChange={() =>
                              act(
                                () => api.setUserOwnLeadsOnly(u.id, !u.own_leads_only),
                                u.own_leads_only
                                  ? `«${u.name}» снова видит всех клиентов.`
                                  : `«${u.name}» теперь видит только своих клиентов.`,
                              )
                            }
                          />
                          только свои клиенты
                        </label>
                      )}
                      {u.role !== "admin" && (
                        <label
                          className="row small nowrap"
                          style={{ gap: 6, marginTop: 4, cursor: "pointer", flexWrap: "nowrap" }}
                          title="Его клиентов видят только он сам и администраторы"
                        >
                          <input
                            type="checkbox"
                            checked={u.private_leads}
                            onChange={() =>
                              act(
                                () => api.setUserPrivateLeads(u.id, !u.private_leads),
                                u.private_leads
                                  ? `Клиенты «${u.name}» снова видны коллегам.`
                                  : `Клиентов «${u.name}» теперь не видят коллеги.`,
                              )
                            }
                          />
                          клиентов не видят коллеги
                        </label>
                      )}
                    </td>
                    <td>
                      {u.disabled ? (
                        <span className="badge red">закрыт</span>
                      ) : (
                        <span className="badge green">открыт</span>
                      )}
                      {u.must_change_password && !u.disabled && (
                        <span className="badge amber" title="Сотрудник ещё не сменил выданный пароль">
                          временный пароль
                        </span>
                      )}
                    </td>
                    <td>
                      {u.telegram_linked ? (
                        <div>
                          <span className="badge green" title={
                            u.telegram_linked_at
                              ? `Привязан ${new Date(u.telegram_linked_at).toLocaleString("ru-RU")}`
                              : ""
                          }>
                            привязан
                          </span>
                          {u.telegram_username && (
                            <div className="muted small mono">@{u.telegram_username}</div>
                          )}
                        </div>
                      ) : (
                        <span className="badge amber">не привязан</span>
                      )}
                    </td>
                    <td className="small nowrap">
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleString("ru-RU")
                        : <span className="muted">ни разу</span>}
                    </td>
                    <td>
                      <div className="row nowrap" style={{ justifyContent: "flex-end" }}>
                        <button className="sm ghost" onClick={() => setEditing(u)}>
                          Править
                        </button>
                        <button className="sm ghost" onClick={() => setLinkFor(u)}>
                          {u.telegram_linked ? "Телеграм" : "Привязать телеграм"}
                        </button>
                        <button className="sm ghost" onClick={() => setPasswordFor(u)}>
                          Сменить пароль
                        </button>
                        <button
                          className="sm ghost"
                          disabled={isMe}
                          title={isMe ? "Нельзя закрыть доступ самому себе" : ""}
                          onClick={() =>
                            act(
                              () => api.setUserDisabled(u.id, !u.disabled),
                              u.disabled
                                ? `Доступ сотруднику «${u.name}» возвращён.`
                                : `Доступ сотруднику «${u.name}» закрыт, активные сеансы прерваны.`,
                            )
                          }
                        >
                          {u.disabled ? "Вернуть доступ" : "Закрыть доступ"}
                        </button>
                        <button
                          className="sm ghost danger"
                          disabled={isMe}
                          title={isMe ? "Нельзя удалить себя" : ""}
                          onClick={() => {
                            if (
                              confirm(
                                `Удалить учётную запись «${u.name}»? Он больше не сможет войти. Данные встреч останутся на месте.`,
                              )
                            ) {
                              act(
                                () => api.deleteUser(u.id),
                                `Учётная запись «${u.name}» удалена.`,
                              );
                            }
                          }}
                        >
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      <div className="card">
        <div className="card-body small muted">
          <strong>Как хранятся пароли.</strong> В базе лежит не пароль, а его
          необратимый отпечаток (scrypt со случайной солью). Прочитать пароль,
          заглянув в базу, нельзя — ни вам, ни тому, кто получит доступ к файлу.
          Поэтому забытый пароль не «подсмотреть», а только выдать новый.
        </div>
      </div>

      {creating && (
        <CreateUserModal
          mailReady={mailReady}
          onClose={() => setCreating(false)}
          onCreated={(name, login, password, mailed, reason) => {
            setCreating(false);
            setNotice(
              mailed
                ? `Сотрудник «${name}» заведён, логин и пароль ушли ему на почту. Запасной вариант, если письмо не дойдёт: логин ${login}, пароль ${password}.`
                : `Сотрудник «${name}» заведён. Передайте ему логин ${login} и пароль ${password} — при первом входе сервис попросит его сменить.${
                    reason && reason !== "не запрошено" ? ` Письмо отправить не удалось: ${reason}.` : ""
                  }`,
            );
            load();
          }}
        />
      )}

      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={(name) => {
            setEditing(null);
            setNotice(`Карточка «${name}» обновлена.`);
            load();
          }}
        />
      )}

      {linkFor && (
        <TelegramLinkModal
          user={linkFor}
          onClose={() => setLinkFor(null)}
          onChanged={(msg) => {
            setLinkFor(null);
            setNotice(msg);
            load();
          }}
        />
      )}

      {passwordFor && (
        <SetPasswordModal
          user={passwordFor}
          mailReady={mailReady}
          onClose={() => setPasswordFor(null)}
          onDone={(password, mailed, reason) => {
            const name = passwordFor.name;
            setPasswordFor(null);
            setNotice(
              mailed
                ? `Новый пароль для «${name}» отправлен ему на почту. Запасной вариант: ${password}.`
                : `Новый пароль для «${name}»: ${password} — передайте его лично, при входе сервис попросит сменить.${
                    reason ? ` Письмо отправить не удалось: ${reason}.` : ""
                  }`,
            );
            load();
          }}
        />
      )}
    </>
  );
}

function CreateUserModal({
  mailReady,
  onClose,
  onCreated,
}: {
  mailReady: boolean;
  onClose: () => void;
  onCreated: (name: string, login: string, password: string, mailed: boolean, reason: string) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState(randomPassword());
  const [role, setRole] = useState<"admin" | "member">("member");
  const [sendEmail, setSendEmail] = useState(mailReady);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.createUser({
        name,
        email,
        login,
        password,
        role,
        send_email: sendEmail,
      });
      onCreated(name, login, password, res.mail.sent, res.mail.reason);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Новый сотрудник"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Отмена</button>
          <button className="primary" form="user-form" type="submit" disabled={busy}>
            {busy && <span className="spinner" />} Завести
          </button>
        </>
      }
    >
      <form id="user-form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="u-name">Имя</label>
          <input
            id="u-name"
            autoFocus
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="u-email">Почта</label>
          <input
            id="u-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div className="muted small" style={{ marginTop: 4 }}>
            На неё Zoom пришлёт приглашение. Этот человек сразу появится в разделе
            «Люди», чтобы его можно было звать на встречи.
          </div>
        </div>
        <div className="grid-2">
          <div className="field">
            <label htmlFor="u-login">Логин</label>
            <input
              id="u-login"
              required
              value={login}
              placeholder="ivanov"
              onChange={(e) => setLogin(e.target.value)}
            />
            <div className="muted small" style={{ marginTop: 4 }}>
              Латиница, цифры, точка, дефис.
            </div>
          </div>
          <div className="field">
            <label htmlFor="u-role">Права</label>
            <select
              id="u-role"
              value={role}
              onChange={(e) => setRole(e.target.value as "admin" | "member")}
            >
              <option value="member">Сотрудник — встречи и люди</option>
              <option value="admin">Администратор — плюс этот раздел</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="u-pass">Пароль</label>
          <div className="row" style={{ flexWrap: "nowrap" }}>
            <input
              id="u-pass"
              required
              className="mono"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="sm nowrap"
              onClick={() => setPassword(randomPassword())}
            >
              Другой
            </button>
          </div>
          <div className="muted small" style={{ marginTop: 4 }}>
            Запишите или скопируйте: после сохранения показать пароль снова будет
            нельзя. Сотрудник сменит его при первом входе.
          </div>
        </div>

        <label className="check-row">
          <input
            type="checkbox"
            checked={sendEmail}
            disabled={!mailReady}
            onChange={(e) => setSendEmail(e.target.checked)}
          />
          <span>
            Выслать логин и пароль на почту сотрудника
            <span className="muted small" style={{ display: "block" }}>
              {mailReady
                ? "Письмо уйдёт сразу после сохранения. Пароль в нём открытым текстом — поэтому он временный и меняется при первом входе."
                : "Почта не настроена: администратору нужно указать SMTP в настройках сервера."}
            </span>
          </span>
        </label>

        {error && <div className="error-text">{error}</div>}
      </form>
    </Modal>
  );
}

function SetPasswordModal({
  user,
  mailReady,
  onClose,
  onDone,
}: {
  user: StaffUser;
  mailReady: boolean;
  onClose: () => void;
  onDone: (password: string, mailed: boolean, reason: string) => void;
}) {
  const [password, setPassword] = useState(randomPassword());
  const [sendEmail, setSendEmail] = useState(mailReady);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.setUserPassword(user.id, password, sendEmail);
      onDone(password, res.mail.sent, res.mail.reason);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Новый пароль для «${user.name}»`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Отмена</button>
          <button className="primary" form="pass-form" type="submit" disabled={busy}>
            {busy && <span className="spinner" />} Выдать пароль
          </button>
        </>
      }
    >
      <form id="pass-form" onSubmit={submit}>
        <p className="small muted">
          Все текущие сеансы этого сотрудника будут прерваны, а при следующем входе
          сервис попросит придумать постоянный пароль.
        </p>
        <label className="check-row" style={{ marginBottom: 14 }}>
          <input
            type="checkbox"
            checked={sendEmail}
            disabled={!mailReady}
            onChange={(e) => setSendEmail(e.target.checked)}
          />
          <span>
            Выслать новый пароль на {user.email}
            {!mailReady && (
              <span className="muted small" style={{ display: "block" }}>
                Почта не настроена.
              </span>
            )}
          </span>
        </label>
        <div className="field">
          <label htmlFor="np">Пароль</label>
          <div className="row" style={{ flexWrap: "nowrap" }}>
            <input
              id="np"
              required
              autoFocus
              className="mono"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="sm nowrap"
              onClick={() => setPassword(randomPassword())}
            >
              Другой
            </button>
          </div>
        </div>
        {error && <div className="error-text">{error}</div>}
      </form>
    </Modal>
  );
}

function TelegramLinkModal({
  user,
  onClose,
  onChanged,
}: {
  user: StaffUser;
  onClose: () => void;
  onChanged: (message: string) => void;
}) {
  const [link, setLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .userTelegramLink(user.id)
      .then((r) => setLink(r.link_url))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [user.id]);

  return (
    <Modal
      title={`Телеграм — ${user.name}`}
      onClose={onClose}
      footer={
        <>
          {user.telegram_linked && (
            <button
              className="danger"
              onClick={() =>
                api
                  .userTelegramUnlink(user.id)
                  .then(() => onChanged(`Телеграм сотрудника «${user.name}» отвязан.`))
                  .catch((e) => setError(e.message))
              }
            >
              Отвязать
            </button>
          )}
          <button onClick={onClose}>Закрыть</button>
        </>
      }
    >
      {error && <div className="banner red">{error}</div>}

      {user.telegram_linked ? (
        <>
          <div className="banner blue" style={{ marginBottom: 14 }}>
            <span>✅</span>
            <div>
              <strong>Телеграм привязан</strong>
              {user.telegram_username && <>Аккаунт @{user.telegram_username}. </>}
              Напоминания о встречах приходят в этот чат.
            </div>
          </div>
          <p className="small muted">
            Если сотрудник сменил аккаунт в телеграме — отвяжите текущий и выдайте
            новую ссылку.
          </p>
        </>
      ) : loading ? (
        <div className="empty">
          <span className="spinner" /> Готовлю ссылку…
        </div>
      ) : (
        <>
          <p className="small">
            Отправьте эту ссылку сотруднику. Он откроет её, нажмёт в телеграме
            «Запустить» — и напоминания начнут приходить.
          </p>
          <div className="field">
            <div className="row" style={{ flexWrap: "nowrap" }}>
              <input readOnly className="mono" value={link ?? ""} />
              <button
                className="sm nowrap"
                onClick={() => {
                  if (link) {
                    navigator.clipboard.writeText(link);
                    setCopied(true);
                  }
                }}
              >
                {copied ? "Скопировано" : "Копировать"}
              </button>
            </div>
          </div>
          <div className="banner amber">
            <span>⚠️</span>
            <div>
              Ссылка персональная — передавайте её лично, как пароль. Кто откроет
              ссылку, тот и будет получать напоминания этого сотрудника.
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: StaffUser;
  onClose: () => void;
  onSaved: (name: string) => void;
}) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.updateUser(user.id, { name, email });
      onSaved(name);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Карточка — ${user.login}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Отмена</button>
          <button className="primary" form="edit-user-form" type="submit" disabled={busy}>
            {busy && <span className="spinner" />} Сохранить
          </button>
        </>
      }
    >
      <form id="edit-user-form" onSubmit={submit}>
        <div className="field">
          <label htmlFor="e-name">Имя</label>
          <input
            id="e-name"
            autoFocus
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="e-email">Почта</label>
          <input
            id="e-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <div className="muted small" style={{ marginTop: 4 }}>
            По этой почте сервис понимает, кому из приглашённых на встречу слать
            напоминание в телеграм. Она же уходит в приглашение Zoom.
          </div>
        </div>
        <div className="muted small">
          Логин <span className="mono">{user.login}</span> и пароль здесь не
          меняются — для пароля есть отдельная кнопка.
        </div>
        {error && <div className="error-text">{error}</div>}
      </form>
    </Modal>
  );
}
